import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProfileLink } from "../Profile/ProfileLink";
import { useProfileGlobalModal } from "../Profile/ProfileGlobalModalContext";
import { UserAvatar } from "../Profile/UserAvatar";
import { AvatarWithXpCrown } from "../Xp/AvatarWithXpCrown";
import { avatarFrameProps, resolveAvatarFrameSrc } from "../Profile/avatarFrame";
import { resolveAvatarUrl } from "../../utils/sessionUser";
import { playerHandleName } from "../../utils/friendDisplayName";
import { formatMatchNameAbbreviated } from "../../utils/matchNameDisplay";
import {
  hasReadableActivityMatchName,
  traderActivityMatchHref,
  traderLobbyHref,
  traderUrlSportParam,
} from "../../utils/traderActivityNavigation";
import type { ProfileModalLocationState } from "../../utils/profileModalNavigation";
import { isProfileHref } from "../../utils/profileModalNavigation";
import {
  formatTimeAgo,
  parseXpEntityRef,
  NOTIFICATION_ACTIVITY_STREAM_ROW_GRID_CLASS,
  NOTIFICATION_CTA_PILL_CLASS,
  NOTIFICATION_MAIN_TEXT_PAD,
  NOTIFICATION_AVATAR_STREAM_CLASS,
  NOTIFICATION_STREAM_BODY_CLASS,
  NOTIFICATION_STREAM_NAME_CLASS,
  NOTIFICATION_STREAM_TIME_SIDE_CLASS,
  MY_ZONE_ACTIVITY_PANEL_AVATAR_CLASS,
  MY_ZONE_ACTIVITY_PANEL_BODY_CLASS,
  MY_ZONE_ACTIVITY_PANEL_CTA_CLASS,
  MY_ZONE_ACTIVITY_PANEL_NAME_CLASS,
  MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS,
  MY_ZONE_ACTIVITY_PANEL_TIME_CLASS,
} from "../notifications/notificationDisplay";
import { NotificationItemRow as NotificationItemRowBase } from "../notifications/NotificationItemRow";
import { isAcknowledgedFriendChallenge } from "../notifications/notificationTaxonomy";
import { formatXp, formatXpHistoryReasonDetail, XP_ICON_SRC } from "../../utils/xp";

/**
 * Friends Activity merges `friend_earned_xp` notifications with digest `recentXpResults`.
 * Without dedupe, the same grant (e.g. daily Trader leaderboard) appears twice.
 */
function friendXpDigestCoverageKeys(digests: FriendDigest[]): Set<string> {
  const keys = new Set<string>();
  for (const f of digests) {
    const uid = Number(f.userId);
    if (!uid) continue;
    for (const xp of f.recentXpResults ?? []) {
      const reason = String(xp?.reason ?? "").trim();
      if (!reason || reason === "xp_friends_batch" || reason.startsWith("xp_friends_batch:")) continue;
      keys.add(`${uid}::${reason}`);
    }
  }
  return keys;
}

function isFriendEarnedXpCoveredByDigest(
  notification: Record<string, unknown>,
  covered: Set<string>,
): boolean {
  if (notification.type !== "friend_earned_xp") return false;
  const sender = notification.sender as { userId?: unknown } | null | undefined;
  const senderId = Number(
    (notification as { sender_id?: unknown }).sender_id ?? sender?.userId,
  );
  if (!senderId) return false;
  const { reason } = parseXpEntityRef(
    typeof notification.entity_ref === "string" ? notification.entity_ref : "",
  );
  const r = String(reason ?? "").trim();
  if (!r || r === "xp_friends_batch" || r.startsWith("xp_friends_batch:")) return false;
  return covered.has(`${senderId}::${r}`);
}

/** Same CTA pill as notification rows — used by `PublicProfile` custom rows. */
export { NOTIFICATION_CTA_PILL_CLASS as ACTIVITY_ACTION_BUTTON } from "../notifications/notificationDisplay";

const NotificationItemRow = NotificationItemRowBase as unknown as React.FC<{
  notification: Record<string, unknown>;
  isUnread?: boolean;
  onActivate?: () => void;
  includeUnreadRing?: boolean;
  hideTime?: boolean;
  variant?: "default" | "panel";
}>;

const ACTIVITY_CTA_RAIL_PLACEHOLDER_CLASS =
  "inline-flex h-[22px] min-w-[4rem] w-[4rem] shrink-0 sm:min-w-[5.25rem] sm:w-[5.25rem]";

const ACTIVITY_PANEL_CTA_RAIL_PLACEHOLDER_CLASS =
  "inline-flex h-[30px] min-w-[4rem] w-[4rem] shrink-0 sm:min-w-[5.25rem] sm:w-[5.25rem]";

/** Profile modal: tighter than drawer — less space between avatar and copy / time and CTA. */
const ACTIVITY_STREAM_MAIN_TEXT_PAD_PROFILE = "min-h-0 py-0 pl-0 pr-0.5 sm:pr-1";

type AvatarFrame = ReturnType<typeof avatarFrameProps>;

export interface FriendDigest {
  userId: number;
  player_id?: string | null;
  username: string;
  player_name?: string | null;
  display_name?: string | null;
  avatar?: string | null;
  profile_pic?: string | null;
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
  traderGamesPlayed: number;
  traderGamesWon: number;
  winRatio: number | null;
  lastPlayedGame?: string | null;
  lastPlayedSport?: string | null;
  lastPlayedAt?: string | null;
  currentStreak?: number;
  bestStreak?: number;
  /** `settled_at` of the newest trade in the current win streak (trader-only). */
  currentStreakNewestSettledAt?: string | null;
  /** `settled_at` of the newest trade in the best consecutive win run (trader-only). */
  bestStreakNewestSettledAt?: string | null;
  latestTraderResult?: {
    sport?: string | null;
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    outcome?: string | null;
    net_coins?: number;
  } | null;
  latestSquaresResult?: {
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    outcome?: string | null;
    over_number?: number | null;
    net_coins?: number;
  } | null;
  recentTraderResults?: Array<{
    sport?: string | null;
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    outcome?: string | null;
    net_coins?: number;
  }>;
  recentSquaresResults?: Array<{
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    outcome?: string | null;
    over_number?: number | null;
    net_coins?: number;
  }>;
  latestBullionResult?: {
    match_id?: string | null;
    match_name?: string | null;
    game_id?: number | null;
    visibility?: string | null;
    settled_at?: string | null;
    net_coins?: number;
  } | null;
  recentBullionResults?: Array<{
    match_id?: string | null;
    match_name?: string | null;
    game_id?: number | null;
    visibility?: string | null;
    settled_at?: string | null;
    net_coins?: number;
  }>;
  latestBullionPersonalBest?: {
    correct_picks: number;
    settled_at?: string | null;
    match_id?: string | null;
    game_id?: number | null;
    match_name?: string | null;
  } | null;
  latestSixesResult?: {
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    net_coins?: number;
    wins_count?: number;
    sets_count?: number;
    multiplier_sixes?: number | null;
  } | null;
  recentSixesResults?: Array<{
    match_id?: string | null;
    match_name?: string | null;
    settled_at?: string | null;
    net_coins?: number;
    wins_count?: number;
    sets_count?: number;
    multiplier_sixes?: number | null;
  }>;
  recentXpResults?: Array<{
    amount?: number;
    reason?: string | null;
    created_at?: string | null;
  }>;
  recentXpWeekPodium?: Array<{
    period_id?: number;
    rank?: number;
    rank_label?: string | null;
    crown?: string | null;
    xp_value?: number;
    period_label?: string | null;
    awarded_at?: string | null;
  }>;
}

export interface FriendListEntry {
  userId: number;
  player_id?: string | null;
  username: string;
  player_name?: string | null;
  display_name?: string | null;
  avatar?: string | null;
  profile_pic?: string | null;
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
  active_play?: {
    trader?: {
      match_id: string;
      sport: string;
      match_name?: string | null;
      started_at?: string | null;
    } | null;
    squares?: {
      match_id: string;
      started_at?: string | null;
      over_number?: number | null;
      match_name?: string | null;
    } | null;
    bullion?: {
      match_id: string;
      game_id: number;
      visibility?: string | null;
      match_name?: string | null;
      started_at?: string | null;
    } | null;
    sixes?: {
      match_id: string;
      match_name?: string | null;
      started_at?: string | null;
    } | null;
  } | null;
  active_plays?: {
    trader?: Array<{
      match_id: string;
      sport: string;
      match_name?: string | null;
      started_at?: string | null;
    }> | null;
    squares?: Array<{
      match_id: string;
      started_at?: string | null;
      over_number?: number | null;
      match_name?: string | null;
    }> | null;
    bullion?: Array<{
      match_id: string;
      game_id: number;
      visibility?: string | null;
      match_name?: string | null;
      started_at?: string | null;
    }> | null;
    sixes?: Array<{
      match_id: string;
      match_name?: string | null;
      started_at?: string | null;
    }> | null;
  } | null;
}

export interface MyTradeGame {
  match_id?: string;
  match_name?: string | null;
  sport?: string | null;
  tstamp?: string | null;
  created_at?: string | null;
  settled?: boolean;
  settled_at?: string | null;
  outcome?: string | null;
  stake?: number | string | null;
  return?: number | string | null;
}

export interface MySquareGame {
  match_id?: string;
  match_name?: string | null;
  created_at?: string | null;
  settled?: boolean;
  settled_at?: string | null;
  outcome?: string | null;
  stake?: number | string | null;
  return?: number | string | null;
  over?: number | null;
  over_number?: number | null;
}

function bullionPlayTo(matchId: string, gameId: number): string {
  const q = new URLSearchParams();
  q.set("match", matchId);
  q.set("game", String(gameId));
  return `/bullion?${q.toString()}`;
}

function traderPlayTo(matchId: string, sportRaw?: string | null): string {
  const sport = traderUrlSportParam(String(sportRaw || "cricket").trim().toLowerCase());
  if (sport === "horses") {
    return `/trader?raceId=${encodeURIComponent(matchId)}&sport=horses`;
  }
  return `/trader?matchId=${encodeURIComponent(matchId)}&sport=${encodeURIComponent(sport)}`;
}

function toTimeValue(value?: string | null): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

type SettledResultLike = {
  settled_at?: string | null;
  match_id?: string | null;
  outcome?: string | null;
  net_coins?: number;
  sport?: string | null;
  over_number?: number | null;
};

/** Dedupe key for friend settled-result rows (multiple trades can share match + settled_at). */
function settledResultIdentity(row: SettledResultLike): string {
  return [
    String(row.settled_at ?? ""),
    String(row.match_id ?? ""),
    String(row.outcome ?? ""),
    String(Number(row.net_coins ?? 0)),
    String(row.sport ?? ""),
    row.over_number != null ? String(row.over_number) : "",
  ].join("\u001f");
}

function settledResultReactKey(prefix: string, userId: number, row: SettledResultLike, seq: number): string {
  return `${prefix}-${userId}-${settledResultIdentity(row).replace(/\u001f/g, "-")}-${seq}`;
}

function formatCoinAmount(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(Math.abs(n)));
}

function normalizeGameLabel(value: unknown): "TRADER" | "SQUARES" | "BULLION" | "SIXES" {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "SQUARES") return "SQUARES";
  if (raw === "BULLION") return "BULLION";
  if (raw === "SIXES") return "SIXES";
  return "TRADER";
}

function gameIconFor(label: "TRADER" | "SQUARES" | "BULLION" | "SIXES"): string {
  if (label === "SQUARES") return "/assets/squares.png";
  if (label === "BULLION") return "/assets/bullion.png";
  if (label === "SIXES") return "/assets/sixes.png";
  return "/assets/trader.png";
}

function toOverInt(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const i = Math.trunc(n);
  if (i <= 0) return null;
  return i;
}

function isNearInTime(aMs: number, bMs: number, windowMs = 6 * 60 * 60 * 1000): boolean {
  if (!aMs || !bMs) return false;
  return Math.abs(aMs - bMs) <= windowMs;
}

const RECORD_STREAK_ACTIVITY_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

function isRecentIsoWithin(iso: string | null | undefined, windowMs: number): boolean {
  const ts = toTimeValue(iso);
  if (!ts) return false;
  return Date.now() - ts <= windowMs;
}

function activityMatchTitleForRow(
  matchName: string | null | undefined,
  matchId?: string | null
): string {
  const id = matchId?.trim() ?? "";
  if (hasReadableActivityMatchName(matchName, id)) {
    const a = formatMatchNameAbbreviated(matchName);
    if (a) return a;
  }
  return "a match";
}

type StartedPlayingSquaresRow = {
  key: string;
  ts: number;
  name: string;
  profileTo: string;
  gameLabel: "SQUARES";
  avatar: string | null | undefined;
  xp_week_crown?: string | null;
  avatarFrame: AvatarFrame;
  matchId: string | null;
  matchTo: string | null;
  overNumber: number | null;
  matchName: string | null;
  startedAt: string | null;
};

/** One timeline row per friend per Squares match; prefer a shared over with the viewer when possible. */
function mergeFriendSquaresStartedRows(
  rows: StartedPlayingSquaresRow[],
  friendUserId: number,
  myActiveSquares: MySquareGame[],
): StartedPlayingSquaresRow[] {
  const byMatch = new Map<string, StartedPlayingSquaresRow[]>();
  for (const r of rows) {
    const mid = String(r.matchId || "").trim();
    if (!mid) continue;
    const list = byMatch.get(mid);
    if (list) list.push(r);
    else byMatch.set(mid, [r]);
  }
  const out: StartedPlayingSquaresRow[] = [];
  for (const [matchId, group] of byMatch) {
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const myOvers = new Set<number>();
    for (const s of myActiveSquares) {
      if (String(s.match_id || "").trim() !== matchId) continue;
      const o = toOverInt(s.over ?? s.over_number ?? null);
      if (o != null) myOvers.add(o);
    }
    const withOver = group.map((g) => ({ row: g, overInt: toOverInt(g.overNumber) }));
    let chosenOver: number | null = null;
    if (myOvers.size > 0) {
      const hit = withOver.filter((x) => x.overInt != null && myOvers.has(x.overInt));
      if (hit.length > 0) {
        hit.sort((a, b) => b.row.ts - a.row.ts);
        chosenOver = hit[0].overInt;
      }
    }
    if (chosenOver == null) {
      const distinct = new Set(
        withOver.map((x) => x.overInt).filter((o): o is number => o != null),
      );
      if (distinct.size === 1) {
        chosenOver = distinct.values().next().value ?? null;
      }
    }
    const bestTs = Math.max(...group.map((g) => g.ts));
    const pickRow =
      chosenOver != null
        ? (withOver.find((x) => x.overInt === chosenOver)?.row ?? group[0])
        : group.reduce((a, b) => (a.ts >= b.ts ? a : b));
    out.push({
      key: `started-sq-${friendUserId}-${matchId}-merged`,
      ts: bestTs,
      name: group[0].name,
      profileTo: group[0].profileTo,
      gameLabel: "SQUARES",
      avatar: group[0].avatar,
      xp_week_crown: group[0].xp_week_crown ?? null,
      avatarFrame: group[0].avatarFrame,
      matchId,
      matchTo: `/squares?match=${encodeURIComponent(matchId)}`,
      overNumber: chosenOver,
      matchName: pickRow.matchName ?? group[0].matchName,
      startedAt: pickRow.startedAt ?? group[0].startedAt ?? null,
    });
  }
  return out;
}

export function mapDigestRows(data: { activity?: unknown }): FriendDigest[] {
  const rows = (data.activity ?? []) as Array<{
    friend: FriendDigest;
    traderGamesPlayed: number;
    traderGamesWon: number;
    winRatio?: number | null;
  }>;
  return rows
    .filter((r) => r.friend)
    .map((r) => ({
      ...r.friend,
      traderGamesPlayed: r.traderGamesPlayed,
      traderGamesWon: r.traderGamesWon,
      winRatio: r.winRatio ?? null,
      lastPlayedGame: (r as { lastPlayedGame?: string | null }).lastPlayedGame ?? null,
      lastPlayedSport: (r as { lastPlayedSport?: string | null }).lastPlayedSport ?? null,
      lastPlayedAt: (r as { lastPlayedAt?: string | null }).lastPlayedAt ?? null,
      currentStreak: (r as { currentStreak?: number }).currentStreak ?? 0,
      bestStreak: (r as { bestStreak?: number }).bestStreak ?? 0,
      currentStreakNewestSettledAt:
        (r as { currentStreakNewestSettledAt?: string | null }).currentStreakNewestSettledAt ?? null,
      bestStreakNewestSettledAt:
        (r as { bestStreakNewestSettledAt?: string | null }).bestStreakNewestSettledAt ?? null,
      latestTraderResult:
        (r as { latestTraderResult?: FriendDigest["latestTraderResult"] }).latestTraderResult ?? null,
      latestSquaresResult:
        (r as { latestSquaresResult?: FriendDigest["latestSquaresResult"] }).latestSquaresResult ?? null,
      recentTraderResults:
        (r as { recentTraderResults?: FriendDigest["recentTraderResults"] }).recentTraderResults ?? [],
      recentSquaresResults:
        (r as { recentSquaresResults?: FriendDigest["recentSquaresResults"] }).recentSquaresResults ?? [],
      latestBullionResult:
        (r as { latestBullionResult?: FriendDigest["latestBullionResult"] }).latestBullionResult ?? null,
      recentBullionResults:
        (r as { recentBullionResults?: FriendDigest["recentBullionResults"] }).recentBullionResults ?? [],
      latestBullionPersonalBest:
        (r as { latestBullionPersonalBest?: FriendDigest["latestBullionPersonalBest"] })
          .latestBullionPersonalBest ?? null,
      latestSixesResult:
        (r as { latestSixesResult?: FriendDigest["latestSixesResult"] }).latestSixesResult ?? null,
      recentSixesResults:
        (r as { recentSixesResults?: FriendDigest["recentSixesResults"] }).recentSixesResults ?? [],
      recentXpResults:
        (r as { recentXpResults?: FriendDigest["recentXpResults"] }).recentXpResults ?? [],
      recentXpWeekPodium:
        (r as { recentXpWeekPodium?: FriendDigest["recentXpWeekPodium"] }).recentXpWeekPodium ?? [],
    }));
}

export interface FriendActivityTimelineProps {
  digests: FriendDigest[];
  liveFriends: FriendListEntry[];
  myTrades: MyTradeGame[];
  mySquares: MySquareGame[];
  notifications: Array<Record<string, unknown>>;
  profileModalState: ProfileModalLocationState | undefined;
  loading: boolean;
  visibleCount: number;
  /** drawer / panel: show notifications + Profile CTAs on digest rows; profile: match CTAs only */
  surface: "drawer" | "profile" | "panel";
  onNotificationRead?: (notificationId: number) => void;
  loadingMore?: boolean;
  emptyMessage?: string;
  /** e.g. profile modal: close overlay when navigating */
  onProfileLinkClick?: () => void;
  /** Report merged timeline row count (for drawer load-more vs expand-visible) */
  onTotalRowsChange?: (totalRows: number) => void;
  /** Auto-load additional rows when the bottom of the list scrolls into view. */
  enableInfiniteScroll?: boolean;
  /** Number of rows to add per infinite-scroll load. */
  infiniteScrollStep?: number;
  /** True when viewer is looking at their own profile activity stream. */
  isOwnProfileView?: boolean;
  /** Server indicates more activity can be fetched for this stream. */
  hasMoreServerRows?: boolean;
  /** True while parent is fetching more rows from server. */
  serverLoadingMore?: boolean;
  /** Triggered when user reaches the end and server has more rows. */
  onReachEnd?: () => void;
}

function ActivityStreamRow({
  surface,
  profileTo,
  profileModalState,
  onProfileLinkClick,
  avatarSrc,
  displayName,
  avatarColor,
  firstLetter,
  xpWeekCrown,
  avatarFrame,
  middle,
  timeLabel,
  ctaSlot,
}: {
  surface: "drawer" | "profile" | "panel";
  profileTo: string;
  profileModalState: ProfileModalLocationState | undefined;
  onProfileLinkClick?: () => void;
  avatarSrc?: string | null;
  displayName?: string | null;
  avatarColor?: string | null;
  firstLetter?: string | null;
  xpWeekCrown?: string | null;
  avatarFrame?: AvatarFrame | null;
  middle: React.ReactNode;
  timeLabel: string;
  ctaSlot: React.ReactNode;
}) {
  const isPanel = surface === "panel";
  const rowGridClass = isPanel
    ? MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS
    : NOTIFICATION_ACTIVITY_STREAM_ROW_GRID_CLASS;
  const avatarClass = isPanel ? MY_ZONE_ACTIVITY_PANEL_AVATAR_CLASS : NOTIFICATION_AVATAR_STREAM_CLASS;
  const frameSrc = resolveAvatarFrameSrc(
    isPanel ? "md" : "sm",
    avatarFrame?.avatarFrameUrl,
    avatarFrame?.avatarFrameSmallUrl,
  );
  const timeClass = isPanel ? MY_ZONE_ACTIVITY_PANEL_TIME_CLASS : NOTIFICATION_STREAM_TIME_SIDE_CLASS;
  const mainTextPadClass = isPanel
    ? "min-h-0 py-0 pl-1 pr-1"
    : surface === "profile"
      ? ACTIVITY_STREAM_MAIN_TEXT_PAD_PROFILE
      : NOTIFICATION_MAIN_TEXT_PAD;
  const timePrClass = isPanel ? "" : surface === "profile" ? "pr-0 sm:pr-0.5" : "pr-0.5 sm:pr-1";
  const linkGapClass = isPanel ? "gap-2" : "gap-1 sm:gap-1.5";
  const sideGapClass = isPanel ? "gap-1" : "gap-1 sm:gap-1.5";

  return (
    <div className={rowGridClass}>
      <ProfileLink
        to={profileTo}
        state={profileModalState}
        onClick={onProfileLinkClick}
        className={`col-span-2 grid [grid-template-columns:subgrid] min-h-0 min-w-0 items-center text-left ${linkGapClass}`}
      >
        <div className={avatarClass}>
          <AvatarWithXpCrown crown={xpWeekCrown} frameSrc={frameSrc} className="block h-full w-full overflow-hidden">
            <UserAvatar
              src={avatarSrc}
              displayName={displayName}
              avatarColor={avatarColor}
              firstLetter={firstLetter}
              size="fill"
              withCrown={false}
            />
          </AvatarWithXpCrown>
        </div>
        <div className={`min-w-0 self-center ${mainTextPadClass}`}>{middle}</div>
      </ProfileLink>
      <div className={`flex min-h-0 shrink-0 flex-row items-center justify-end self-center ${sideGapClass}`}>
        {timeLabel ? (
          <span
            className={`${timeClass} inline-block max-w-[4rem] shrink-0 truncate tabular-nums sm:max-w-[4.5rem] ${timePrClass}`}
          >
            {timeLabel}
          </span>
        ) : null}
        {ctaSlot}
      </div>
    </div>
  );
}

function ActivitySkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <ul className="space-y-1.5" aria-hidden>
      {Array.from({ length: Math.max(1, count) }).map((_, idx) => (
        <li key={`activity-skeleton-${idx}`} className="overflow-hidden">
          <div className={NOTIFICATION_ACTIVITY_STREAM_ROW_GRID_CLASS}>
            <div className="col-span-2 grid [grid-template-columns:subgrid] min-h-0 min-w-0 items-center gap-1 sm:gap-1.5">
              <div className={`${NOTIFICATION_AVATAR_STREAM_CLASS} animate-pulse bg-white/10`} />
              <div className="min-w-0 self-center py-0.5 pr-1">
                <div className="h-3 w-[85%] animate-pulse bg-white/12" />
                <div className="mt-1 h-3 w-[60%] animate-pulse bg-white/8" />
              </div>
            </div>
            <div className="flex min-h-0 shrink-0 flex-row items-center justify-end gap-1 self-center sm:gap-1.5">
              <div className="h-3 w-10 animate-pulse bg-white/12" />
              <div className="h-[22px] w-[4.5rem] animate-pulse bg-white/10 sm:w-[5.25rem]" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default function FriendActivityTimeline({
  digests,
  liveFriends,
  myTrades,
  mySquares,
  notifications,
  profileModalState,
  loading,
  visibleCount,
  surface,
  onNotificationRead,
  loadingMore = false,
  emptyMessage = "No recent activity yet. Add friends — updates will show here.",
  onProfileLinkClick,
  onTotalRowsChange,
  enableInfiniteScroll = false,
  infiniteScrollStep = 10,
  isOwnProfileView = false,
  hasMoreServerRows = false,
  serverLoadingMore = false,
  onReachEnd,
}: FriendActivityTimelineProps) {
  const navigate = useNavigate();
  const { dismissProfileModalForNavigation } = useProfileGlobalModal();
  const handleActivityCtaClick = useCallback(
    (e: React.MouseEvent, to: string, linkState?: ProfileModalLocationState) => {
      if (isProfileHref(to)) {
        onProfileLinkClick?.();
        return;
      }
      e.preventDefault();
      dismissProfileModalForNavigation();
      navigate(to, linkState ? { state: linkState } : undefined);
    },
    [dismissProfileModalForNavigation, navigate, onProfileLinkClick]
  );
  const [infiniteVisibleCount, setInfiniteVisibleCount] = useState(visibleCount);
  const infiniteSentinelRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    setInfiniteVisibleCount(visibleCount);
  }, [visibleCount, enableInfiniteScroll]);

  const renderGameToken = (gameRaw: unknown) => {
    const game = normalizeGameLabel(gameRaw);
    return (
      <span className="inline-flex items-center gap-1 align-middle font-anton text-[10px] font-normal uppercase leading-[18px] sm:text-[11px] sm:leading-[20px]">
        <img
          src={gameIconFor(game)}
          alt=""
          className="h-4 w-4 object-contain sm:h-[18px] sm:w-[18px]"
          loading="lazy"
          decoding="async"
        />
        <span>{game}</span>
      </span>
    );
  };

  const { digestRows, settledResultRows, xpResultRows, startedPlayingRows, timelineRows } = useMemo(() => {
    const xpDigestCovered = friendXpDigestCoverageKeys(digests);
    const timelineNotifications = notifications.filter(
      (n) =>
        !isAcknowledgedFriendChallenge(n) &&
        !isFriendEarnedXpCoveredByDigest(n, xpDigestCovered),
    );
    const notificationRowsInner = timelineNotifications.map((n) => ({
      key: `notif-${String((n as { id?: number }).id ?? Math.random())}`,
      ts: toTimeValue((n as { created_at?: string }).created_at ?? null),
      node: n,
    }));

    const myActiveSquaresInner = mySquares.filter((s) => s.settled !== true);

    const startedPlayingRowsInner = liveFriends.flatMap((f) => {
      const name = playerHandleName(f);
      const profileTo = f.player_id
        ? `/profile?p=${encodeURIComponent(f.player_id)}`
        : `/profile?u=${f.userId}`;
      const avatar = resolveAvatarUrl(f);
      const xpWeekCrown = f.xp_week_crown ?? null;
      const avatarFrame = avatarFrameProps(f);

      const traderRowsRaw = Array.isArray(f.active_plays?.trader)
        ? f.active_plays?.trader || []
        : f.active_play?.trader
          ? [f.active_play.trader]
          : [];
      const squaresRowsRaw = Array.isArray(f.active_plays?.squares)
        ? f.active_plays?.squares || []
        : f.active_play?.squares
          ? [f.active_play.squares]
          : [];
      const bullionRowsRaw = Array.isArray(f.active_plays?.bullion)
        ? f.active_plays?.bullion || []
        : f.active_play?.bullion
          ? [f.active_play.bullion]
          : [];
      const sixesRowsRaw = Array.isArray(f.active_plays?.sixes)
        ? f.active_plays?.sixes || []
        : f.active_play?.sixes
          ? [f.active_play.sixes]
          : [];

      const traderRows = traderRowsRaw.map((t) => ({
        key: `started-tr-${f.userId}-${t.match_id}-${String(t.started_at || "")}`,
        ts: toTimeValue(t.started_at ?? null),
        name,
        profileTo,
        gameLabel: "TRADER" as const,
        avatar,
        xp_week_crown: xpWeekCrown,
        avatarFrame,
        matchId: t.match_id ?? null,
        matchTo: t.match_id ? traderPlayTo(t.match_id, t.sport) : null,
        overNumber: null,
        matchName: t.match_name ?? null,
        startedAt: t.started_at != null ? String(t.started_at) : null,
      }));

      const squaresRows: StartedPlayingSquaresRow[] = squaresRowsRaw.map((s) => ({
        key: `started-sq-${f.userId}-${s.match_id}-${String(s.started_at || "")}`,
        ts: toTimeValue(s.started_at ?? null),
        name,
        profileTo,
        gameLabel: "SQUARES",
        avatar,
        xp_week_crown: xpWeekCrown,
        avatarFrame,
        matchId: s.match_id ?? null,
        matchTo: s.match_id ? `/squares?match=${encodeURIComponent(s.match_id)}` : null,
        overNumber: s.over_number ?? null,
        matchName: s.match_name ?? null,
        startedAt: s.started_at != null ? String(s.started_at) : null,
      }));

      const squaresMerged = mergeFriendSquaresStartedRows(squaresRows, f.userId, myActiveSquaresInner);

      const bullionRows = bullionRowsRaw.map((fv) => {
        const matchId = fv.match_id ?? "";
        const gameId = Number(fv.game_id);
        const isPrivate = String(fv.visibility || "").trim().toLowerCase() === "private";
        return {
          key: `started-fo-${f.userId}-${matchId}-${gameId}-${String(fv.started_at || "")}`,
          ts: toTimeValue(fv.started_at ?? null),
          name,
          profileTo,
          gameLabel: "BULLION" as const,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          matchId: matchId || null,
          matchTo: matchId && Number.isFinite(gameId) && gameId > 0 ? bullionPlayTo(matchId, gameId) : null,
          overNumber: null,
          matchName: fv.match_name ?? null,
          startedAt: fv.started_at != null ? String(fv.started_at) : null,
          bullionEntryKind: isPrivate ? ("private_battle" as const) : ("public_arena" as const),
          ctaTo: isPrivate ? profileTo : matchId && gameId > 0 ? bullionPlayTo(matchId, gameId) : null,
          ctaLabel: isPrivate ? ("PROFILE" as const) : ("PLAY" as const),
        };
      });

      const sixesRows = sixesRowsRaw.map((sv) => {
        const matchId = sv.match_id ?? "";
        return {
          key: `started-sx-${f.userId}-${matchId}-${String(sv.started_at || "")}`,
          ts: toTimeValue(sv.started_at ?? null),
          name,
          profileTo,
          gameLabel: "SIXES" as const,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          matchId: matchId || null,
          matchTo: "/sixes",
          overNumber: null,
          matchName: sv.match_name ?? null,
          startedAt: sv.started_at != null ? String(sv.started_at) : null,
          ctaTo: "/sixes",
          ctaLabel: "PLAY" as const,
        };
      });

      return [...sixesRows, ...bullionRows, ...squaresMerged, ...traderRows];
    });

    const digestRowsInner = digests.flatMap((f) => {
      const name = playerHandleName(f);
      const profileTo = f.player_id
        ? `/profile?p=${encodeURIComponent(f.player_id)}`
        : `/profile?u=${f.userId}`;
      const avatar = resolveAvatarUrl(f) || "";
      const xpWeekCrown = f.xp_week_crown ?? null;
      const avatarFrame = avatarFrameProps(f);
      const baseTs = toTimeValue(f.lastPlayedAt ?? null);
      const currentStreak = Number(f.currentStreak || 0);
      const bestStreak = Number(f.bestStreak || 0);
      const lastPlayedAtIso = f.lastPlayedAt != null ? String(f.lastPlayedAt) : null;
      /** Shown in the row (“Xm” / date) — real `settled_at` of newest trade in the streak / PB run. */
      const streakDisplayIso =
        f.currentStreakNewestSettledAt != null && String(f.currentStreakNewestSettledAt).trim() !== ""
          ? String(f.currentStreakNewestSettledAt)
          : lastPlayedAtIso;
      const pbDisplayIso =
        f.bestStreakNewestSettledAt != null && String(f.bestStreakNewestSettledAt).trim() !== ""
          ? String(f.bestStreakNewestSettledAt)
          : lastPlayedAtIso;

      /**
       * Sort key for merged timeline only: keep digest rows stacked with newest digest timestamp so they are not
       * pushed below `visibleCount` by older real streak timestamps vs. fresh notifications.
       */
      const streakSortTs =
        baseTs > 0 ? baseTs - 1 : Math.max(0, toTimeValue(streakDisplayIso)) || (currentStreak >= 2 ? -1 : 0);
      const pbSortTs =
        baseTs > 0
          ? baseTs - (currentStreak >= 2 ? 2 : 1)
          : Math.max(0, toTimeValue(pbDisplayIso)) || (bestStreak >= 3 ? -2 : 0);

      const out: Array<{
        key: string;
        ts: number;
        name: string;
        profileTo: string;
        avatar: string;
        xp_week_crown?: string | null;
        avatarFrame: AvatarFrame;
        digestVariant: "streak" | "pb" | "bullion_pb";
        streakValue?: number;
        pbValue?: number;
        bullionCorrectPicks?: number;
        lastPlayedAt: string | null;
        digestDisplayAt: string | null;
      }> = [];

      if (currentStreak >= 2) {
        out.push({
          key: `digest-${f.userId}-streak`,
          ts: streakSortTs,
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          digestVariant: "streak",
          streakValue: currentStreak,
          lastPlayedAt: lastPlayedAtIso,
          digestDisplayAt: streakDisplayIso,
        });
      }

      if (bestStreak >= 3 && isRecentIsoWithin(pbDisplayIso, RECORD_STREAK_ACTIVITY_WINDOW_MS)) {
        out.push({
          key: `digest-${f.userId}-pb`,
          ts: pbSortTs,
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          digestVariant: "pb",
          pbValue: bestStreak,
          lastPlayedAt: lastPlayedAtIso,
          digestDisplayAt: pbDisplayIso,
        });
      }

      const bullionPb = f.latestBullionPersonalBest;
      const bullionPbAt =
        bullionPb?.settled_at != null && String(bullionPb.settled_at).trim() !== ""
          ? String(bullionPb.settled_at)
          : null;
      const bullionPbPicks = Number(bullionPb?.correct_picks ?? 0);
      if (
        bullionPb &&
        bullionPbPicks >= 1 &&
        bullionPbAt &&
        isRecentIsoWithin(bullionPbAt, RECORD_STREAK_ACTIVITY_WINDOW_MS)
      ) {
        out.push({
          key: `digest-${f.userId}-bullion-pb`,
          ts: Math.max(0, toTimeValue(bullionPbAt)),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          digestVariant: "bullion_pb",
          bullionCorrectPicks: bullionPbPicks,
          lastPlayedAt: lastPlayedAtIso,
          digestDisplayAt: bullionPbAt,
        });
      }

      return out;
    });

    const settledResultRowsInner = digests.flatMap((f) => {
      const name = playerHandleName(f);
      const profileTo = f.player_id
        ? `/profile?p=${encodeURIComponent(f.player_id)}`
        : `/profile?u=${f.userId}`;
      const avatar = resolveAvatarUrl(f) || "";
      const xpWeekCrown = f.xp_week_crown ?? null;
      const avatarFrame = avatarFrameProps(f);
      const rows: Array<{
        key: string;
        ts: number;
        name: string;
        profileTo: string;
        avatar: string;
        xp_week_crown?: string | null;
        avatarFrame: AvatarFrame;
        gameLabel: "SQUARES" | "TRADER" | "BULLION" | "SIXES";
        didWin: boolean;
        coins: string;
        matchId: string | null;
        matchTo: string | null;
        ctaTo: string | null;
        ctaLabel: string;
        matchLabel: string;
        overNumber: number | null;
        settledAt: string;
      }> = [];

      const sqRows =
        (Array.isArray(f.recentSquaresResults) && f.recentSquaresResults.length > 0
          ? f.recentSquaresResults
          : f.latestSquaresResult
            ? [f.latestSquaresResult]
            : []) || [];
      const seenSquaresResults = new Set<string>();
      for (const sq of sqRows) {
        if (!sq?.settled_at) continue;
        const identity = settledResultIdentity(sq);
        if (seenSquaresResults.has(identity)) continue;
        seenSquaresResults.add(identity);
        const matchId = sq.match_id || "";
        const coinsNum = Number(sq.net_coins || 0);
        // Voids (and break-even) are 0 P&L — do not report as "lost 0 coins".
        if (
          String(sq.outcome ?? "").trim().toUpperCase() === "VOID" ||
          coinsNum === 0
        ) {
          continue;
        }
        rows.push({
          key: settledResultReactKey("result-sq", f.userId, sq, rows.length),
          ts: toTimeValue(sq.settled_at),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          gameLabel: "SQUARES",
          didWin: coinsNum > 0,
          coins: formatCoinAmount(coinsNum),
          matchId: matchId || null,
          matchTo: matchId
            ? `/matches?id=${encodeURIComponent(matchId)}&status=complete`
            : null,
          ctaTo: matchId
            ? `/matches?id=${encodeURIComponent(matchId)}&status=complete`
            : null,
          ctaLabel: "MATCH",
          matchLabel: activityMatchTitleForRow(sq.match_name),
          overNumber: sq.over_number ?? null,
          settledAt: String(sq.settled_at),
        });
      }

      const foRows =
        (Array.isArray(f.recentBullionResults) && f.recentBullionResults.length > 0
          ? f.recentBullionResults
          : f.latestBullionResult
            ? [f.latestBullionResult]
            : []) || [];
      const seenBullionResults = new Set<string>();
      for (const fo of foRows) {
        if (!fo?.settled_at) continue;
        const identity = [
          String(fo.settled_at),
          String(fo.match_id ?? ""),
          String(fo.game_id ?? ""),
          String(Number(fo.net_coins ?? 0)),
        ].join("\u001f");
        if (seenBullionResults.has(identity)) continue;
        seenBullionResults.add(identity);
        const coinsNum = Number(fo.net_coins || 0);
        if (coinsNum <= 0) continue;
        rows.push({
          key: `result-fo-${f.userId}-${identity.replace(/\u001f/g, "-")}-${rows.length}`,
          ts: toTimeValue(fo.settled_at),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          gameLabel: "BULLION",
          didWin: true,
          coins: formatCoinAmount(coinsNum),
          matchId: fo.match_id || null,
          matchTo: null,
          ctaTo: profileTo,
          ctaLabel: "PROFILE",
          matchLabel: activityMatchTitleForRow(fo.match_name),
          overNumber: null,
          settledAt: String(fo.settled_at),
        });
      }

      const sxRows =
        (Array.isArray(f.recentSixesResults) && f.recentSixesResults.length > 0
          ? f.recentSixesResults
          : f.latestSixesResult
            ? [f.latestSixesResult]
            : []) || [];
      const seenSixesResults = new Set<string>();
      for (const sx of sxRows) {
        if (!sx?.settled_at) continue;
        const identity = [
          String(sx.settled_at),
          String(sx.match_id ?? ""),
          String(Number(sx.net_coins ?? 0)),
        ].join("\u001f");
        if (seenSixesResults.has(identity)) continue;
        seenSixesResults.add(identity);
        const coinsNum = Number(sx.net_coins || 0);
        if (coinsNum <= 0) continue;
        rows.push({
          key: `result-sx-${f.userId}-${identity.replace(/\u001f/g, "-")}-${rows.length}`,
          ts: toTimeValue(sx.settled_at),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          gameLabel: "SIXES",
          didWin: true,
          coins: formatCoinAmount(coinsNum),
          matchId: sx.match_id || null,
          matchTo: null,
          ctaTo: profileTo,
          ctaLabel: "PROFILE",
          matchLabel: activityMatchTitleForRow(sx.match_name),
          overNumber: null,
          settledAt: String(sx.settled_at),
        });
      }

      const trRows =
        (Array.isArray(f.recentTraderResults) && f.recentTraderResults.length > 0
          ? f.recentTraderResults
          : f.latestTraderResult
            ? [f.latestTraderResult]
            : []) || [];
      const seenTraderResults = new Set<string>();
      for (const tr of trRows) {
        if (!tr?.settled_at) continue;
        const identity = settledResultIdentity(tr);
        if (seenTraderResults.has(identity)) continue;
        seenTraderResults.add(identity);
        const matchId = tr.match_id || "";
        const coinsNum = Number(tr.net_coins || 0);
        // Voids (and break-even) are 0 P&L — do not report as "lost 0 coins".
        if (
          String(tr.outcome ?? "").trim().toUpperCase() === "VOID" ||
          coinsNum === 0
        ) {
          continue;
        }
        rows.push({
          key: settledResultReactKey("result-tr", f.userId, tr, rows.length),
          ts: toTimeValue(tr.settled_at),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          gameLabel: "TRADER",
          didWin: coinsNum > 0,
          coins: formatCoinAmount(coinsNum),
          matchId: matchId || null,
          matchTo: matchId ? traderActivityMatchHref(matchId, tr.match_name, tr.sport) : traderLobbyHref(tr.sport),
          ctaTo: matchId ? traderActivityMatchHref(matchId, tr.match_name, tr.sport) : traderLobbyHref(tr.sport),
          ctaLabel: "MATCH",
          matchLabel: activityMatchTitleForRow(tr.match_name, matchId),
          overNumber: null,
          settledAt: String(tr.settled_at),
        });
      }

      return rows;
    });

    const xpResultRowsInner = digests.flatMap((f) => {
      const name = playerHandleName(f);
      const profileTo = f.player_id
        ? `/profile?p=${encodeURIComponent(f.player_id)}`
        : `/profile?u=${f.userId}`;
      const avatar = resolveAvatarUrl(f) || "";
      const xpWeekCrown = f.xp_week_crown ?? null;
      const avatarFrame = avatarFrameProps(f);
      const xpRows = Array.isArray(f.recentXpResults) ? f.recentXpResults : [];
      return xpRows
        .filter((xp) => xp?.created_at)
        .map((xp, i) => ({
          key: `xp-${f.userId}-${String(xp.created_at)}-${String(xp.reason ?? "")}-${i}`,
          ts: toTimeValue(xp.created_at ?? null),
          name,
          profileTo,
          avatar,
          xp_week_crown: xpWeekCrown,
          avatarFrame,
          amount: Number(xp.amount) || 0,
          reasonDetail: formatXpHistoryReasonDetail(xp.reason),
          createdAt: String(xp.created_at),
        }));
    });

    const xpWeekPodiumRowsInner = digests.flatMap((f) => {
      const name = playerHandleName(f);
      const profileTo = f.player_id
        ? `/profile?p=${encodeURIComponent(f.player_id)}`
        : `/profile?u=${f.userId}`;
      const avatar = resolveAvatarUrl(f) || "";
      const avatarFrame = avatarFrameProps(f);
      const podiumRows = Array.isArray(f.recentXpWeekPodium) ? f.recentXpWeekPodium : [];
      return podiumRows
        .filter((p) => p?.awarded_at || p?.period_id)
        .map((p, i) => {
          const rank = Number(p.rank) || 0;
          const crown =
            p.crown === "gold" || p.crown === "silver" || p.crown === "bronze"
              ? p.crown
              : rank === 1
                ? "gold"
                : rank === 2
                  ? "silver"
                  : rank === 3
                    ? "bronze"
                    : null;
          return {
            key: `xp-podium-${f.userId}-${String(p.period_id ?? "")}-${i}`,
            ts: toTimeValue(p.awarded_at ?? null),
            name,
            profileTo,
            avatar,
            xp_week_crown: crown,
            avatarFrame,
            rankLabel: String(p.rank_label || "").trim() || "on the podium",
            periodLabel: String(p.period_label || "").trim() || "last week",
            xpValue: Number(p.xp_value) || 0,
            awardedAt: p.awarded_at != null ? String(p.awarded_at) : "",
          };
        });
    });

    const timelineRowsInner = [
      ...notificationRowsInner.map((row) => ({ ...row, kind: "notification" as const })),
      ...digestRowsInner.map((row) => ({ ...row, kind: "digest" as const })),
      ...settledResultRowsInner.map((row) => ({ ...row, kind: "result" as const })),
      ...xpResultRowsInner.map((row) => ({ ...row, kind: "xp" as const })),
      ...xpWeekPodiumRowsInner.map((row) => ({ ...row, kind: "xp_week_podium" as const })),
      ...startedPlayingRowsInner.map((row) => ({ ...row, kind: "started" as const })),
    ].sort((a, b) => b.ts - a.ts);

    return {
      digestRows: digestRowsInner,
      settledResultRows: settledResultRowsInner,
      xpResultRows: xpResultRowsInner,
      startedPlayingRows: startedPlayingRowsInner,
      timelineRows: timelineRowsInner,
    };
  }, [digests, liveFriends, myTrades, mySquares, notifications]);

  const totalTimelineRows = timelineRows.length;
  const effectiveVisibleCount = enableInfiniteScroll ? infiniteVisibleCount : visibleCount;
  const visibleTimelineRows = timelineRows.slice(0, effectiveVisibleCount);
  const hasMoreRows = enableInfiniteScroll && visibleTimelineRows.length < totalTimelineRows;

  useEffect(() => {
    onTotalRowsChange?.(totalTimelineRows);
  }, [totalTimelineRows, onTotalRowsChange]);

  useEffect(() => {
    if (!hasMoreRows) return;
    const target = infiniteSentinelRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        if (visibleTimelineRows.length < totalTimelineRows) {
          setInfiniteVisibleCount((prev) =>
            Math.min(totalTimelineRows, prev + Math.max(1, Math.trunc(infiniteScrollStep))),
          );
          return;
        }
        if (hasMoreServerRows && !serverLoadingMore) {
          onReachEnd?.();
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [
    hasMoreRows,
    totalTimelineRows,
    infiniteScrollStep,
    visibleTimelineRows.length,
    hasMoreServerRows,
    serverLoadingMore,
    onReachEnd,
  ]);

  const myActiveSquares = mySquares.filter((s) => s.settled !== true);
  const myActiveTrades = myTrades.filter((t) => t.settled !== true);
  const mySettledSquares = mySquares.filter((s) => s.settled === true || Boolean(s.settled_at));
  const mySettledTrades = myTrades.filter((t) => t.settled === true || Boolean(t.settled_at));

  const streamBodyClass =
    surface === "panel" ? MY_ZONE_ACTIVITY_PANEL_BODY_CLASS : NOTIFICATION_STREAM_BODY_CLASS;
  const streamNameClass =
    surface === "panel" ? MY_ZONE_ACTIVITY_PANEL_NAME_CLASS : NOTIFICATION_STREAM_NAME_CLASS;
  const streamCtaClass =
    surface === "panel" ? MY_ZONE_ACTIVITY_PANEL_CTA_CLASS : NOTIFICATION_CTA_PILL_CLASS;
  const ctaRailPlaceholderClass =
    surface === "panel" ? ACTIVITY_PANEL_CTA_RAIL_PLACEHOLDER_CLASS : ACTIVITY_CTA_RAIL_PLACEHOLDER_CLASS;
  const showDigestProfileCta = surface === "drawer" || surface === "panel";

  if (loading) {
    if (surface === "profile") {
      return <ActivitySkeletonRows count={4} />;
    }
    return <p className="px-1 py-3 font-chakra text-[14px] text-white/55">Loading activity…</p>;
  }

  return (
    <ul className="space-y-1.5">
      {visibleTimelineRows.map((row) => {
        if (row.kind === "notification") {
          const n = row.node;
          const nid = n.id as number;
          const readAt = (n as { read_at?: string }).read_at;
          return (
            <li key={row.key} className="overflow-hidden">
              <NotificationItemRow
                notification={n}
                isUnread={!readAt}
                onActivate={() => onNotificationRead?.(nid)}
                variant={surface === "panel" ? "panel" : "default"}
              />
            </li>
          );
        }

        if (row.kind === "started") {
          const startedRel = row.startedAt ? formatTimeAgo(row.startedAt) : "";
          const userAlsoOnSquaresSameMatch =
            row.gameLabel === "SQUARES" &&
            myActiveSquares.some((s) => String(s.match_id || "") === String(row.matchId || ""));
          const userAlsoOnTrader =
            row.gameLabel === "TRADER" &&
            myActiveTrades.some((t) => String(t.match_id || "") === String(row.matchId || ""));
          const showBothPlayingSameMatch = userAlsoOnSquaresSameMatch || userAlsoOnTrader;
          const bullionEntryKind = (row as { bullionEntryKind?: "public_arena" | "private_battle" }).bullionEntryKind;
          const startedMiddle =
            row.gameLabel === "BULLION" ? (
              <p className={streamBodyClass}>
                {isOwnProfileView ? (
                  <>
                    <span className={streamNameClass}>You</span>
                    <span className="text-white/90">
                      {" "}
                      entered a {bullionEntryKind === "private_battle" ? "private battle" : "public arena"} on{" "}
                    </span>
                    {renderGameToken("BULLION")}
                    <span className="text-white/90">.</span>
                  </>
                ) : (
                  <>
                    <span className={streamNameClass}>{row.name}</span>
                    <span className="text-white/90">
                      {" "}
                      entered a {bullionEntryKind === "private_battle" ? "private battle" : "public arena"} on{" "}
                    </span>
                    {renderGameToken("BULLION")}
                    <span className="text-white/90">.</span>
                  </>
                )}
              </p>
            ) : row.gameLabel === "SQUARES" ? (
              <p className={streamBodyClass}>
                {isOwnProfileView ? (
                  <>
                    <span className={streamNameClass}>You</span>
                    <span className="text-white/90"> started playing </span>
                    {renderGameToken("SQUARES")}
                    <span className="text-white/90">.</span>
                  </>
                ) : showBothPlayingSameMatch ? (
                  <>
                    <span className="text-white/90">You are both playing </span>
                    {renderGameToken("SQUARES")}
                    <span className="text-white/90">.</span>
                  </>
                ) : (
                  <>
                    <span className={streamNameClass}>{row.name}</span>
                    <span className="text-white/90"> started playing </span>
                    {renderGameToken("SQUARES")}
                    <span className="text-white/90">.</span>
                  </>
                )}
              </p>
            ) : (
              <p className={streamBodyClass}>
                {isOwnProfileView ? (
                  <>
                    <span className={streamNameClass}>You</span>
                    <span className="text-white/90"> started playing </span>
                    {renderGameToken(row.gameLabel)}
                    <span className="text-white/90">.</span>
                  </>
                ) : showBothPlayingSameMatch ? (
                  <>
                    <span className="text-white/90">You are both playing </span>
                    {renderGameToken(row.gameLabel)}
                    <span className="text-white/90">.</span>
                  </>
                ) : (
                  <>
                    <span className={streamNameClass}>{row.name}</span>
                    <span className="text-white/90"> started playing </span>
                    {renderGameToken(row.gameLabel)}
                    <span className="text-white/90">.</span>
                  </>
                )}
              </p>
            );

          return (
            <li key={row.key} className="overflow-hidden">
              <ActivityStreamRow
                surface={surface}
                profileTo={row.profileTo}
                profileModalState={profileModalState}
                onProfileLinkClick={onProfileLinkClick}
                avatarSrc={row.avatar || ""}
                displayName={row.name}
                xpWeekCrown={(row as { xp_week_crown?: string | null }).xp_week_crown}
                avatarFrame={row.avatarFrame}
                middle={startedMiddle}
                timeLabel={startedRel}
                ctaSlot={
                  (() => {
                    const ctaTo =
                      (row as { ctaTo?: string | null }).ctaTo ?? row.matchTo ?? null;
                    const ctaLabel =
                      (row as { ctaLabel?: string }).ctaLabel ??
                      (row.gameLabel === "BULLION" ? "PLAY" : "MATCH");
                    if (!ctaTo) {
                      return <span className={ctaRailPlaceholderClass} aria-hidden />;
                    }
                    return (
                      <ProfileLink
                        to={ctaTo}
                        state={ctaLabel === "PROFILE" ? profileModalState : undefined}
                        className={streamCtaClass}
                        onClick={(e) =>
                          handleActivityCtaClick(
                            e,
                            ctaTo,
                            ctaLabel === "PROFILE" ? profileModalState : undefined
                          )
                        }
                      >
                        {ctaLabel}
                      </ProfileLink>
                    );
                  })()
                }
              />
            </li>
          );
        }

        if (row.kind === "result") {
          const settledRel = formatTimeAgo(row.settledAt);
          const showBothPlayedSameMatch =
            !isOwnProfileView &&
            ((row.gameLabel === "SQUARES" &&
              mySettledSquares.some((s) => String(s.match_id || "") === String(row.matchId || ""))) ||
              (row.gameLabel === "TRADER" &&
                mySettledTrades.some((t) => String(t.match_id || "") === String(row.matchId || ""))));
          const resultMiddle = (
            <p className={streamBodyClass}>
              {isOwnProfileView ? (
                <>
                  <span className={streamNameClass}>You</span>
                  <span className="text-white/90"> </span>
                  <span className="text-white/90">{row.didWin ? "won " : "lost "}</span>
                  <span className={streamNameClass}>{row.coins} gold balls</span>
                  <span className="text-white/90"> on </span>
                  {renderGameToken(row.gameLabel)}
                  <span className="text-white/90">.</span>
                </>
              ) : showBothPlayedSameMatch ? (
                <>
                  <span className="text-white/90">You both played </span>
                  {renderGameToken(row.gameLabel)}
                  <span className="text-white/90">. </span>
                  <span className={streamNameClass}>{row.name}</span>
                  <span className="text-white/90">{row.didWin ? " won " : " lost "}</span>
                  <span className={streamNameClass}>{row.coins} gold balls</span>
                  <span className="text-white/90">.</span>
                </>
              ) : (
                <>
                  <span className={streamNameClass}>{row.name}</span>
                  <span className="text-white/90">{row.didWin ? " won " : " lost "}</span>
                  <span className={streamNameClass}>{row.coins} gold balls</span>
                  <span className="text-white/90"> on </span>
                  {renderGameToken(row.gameLabel)}
                  <span className="text-white/90">.</span>
                </>
              )}
            </p>
          );

          return (
            <li key={row.key} className="overflow-hidden">
              <ActivityStreamRow
                surface={surface}
                profileTo={row.profileTo}
                profileModalState={profileModalState}
                onProfileLinkClick={onProfileLinkClick}
                avatarSrc={row.avatar || ""}
                displayName={row.name}
                xpWeekCrown={(row as { xp_week_crown?: string | null }).xp_week_crown}
                avatarFrame={row.avatarFrame}
                middle={resultMiddle}
                timeLabel={settledRel}
                ctaSlot={
                  row.ctaTo ? (
                    <ProfileLink
                      to={row.ctaTo}
                      state={row.ctaLabel === "PROFILE" ? profileModalState : undefined}
                      className={streamCtaClass}
                      onClick={(e) =>
                        handleActivityCtaClick(
                          e,
                          row.ctaTo!,
                          row.ctaLabel === "PROFILE" ? profileModalState : undefined
                        )
                      }
                    >
                      {row.ctaLabel}
                    </ProfileLink>
                  ) : (
                    <span className={ctaRailPlaceholderClass} aria-hidden />
                  )
                }
              />
            </li>
          );
        }

        if (row.kind === "xp") {
          const xpRel = formatTimeAgo(row.createdAt);
          const xpMiddle = (
            <p className={streamBodyClass}>
              {isOwnProfileView ? (
                <>
                  <span className={streamNameClass}>You</span>
                  <span className="text-white/90"> earned </span>
                  <span className="inline-flex items-center gap-0.5 self-center">
                    <span className={streamNameClass}>{formatXp(row.amount)}</span>
                    <img src={XP_ICON_SRC} alt="" className="h-3 w-3 shrink-0 object-contain" aria-hidden />
                  </span>
                  <span className="text-white/90"> {row.reasonDetail}.</span>
                </>
              ) : (
                <>
                  <span className={streamNameClass}>{row.name}</span>
                  <span className="text-white/90"> earned </span>
                  <span className="inline-flex items-center gap-0.5 self-center">
                    <span className={streamNameClass}>{formatXp(row.amount)}</span>
                    <img src={XP_ICON_SRC} alt="" className="h-3 w-3 shrink-0 object-contain" aria-hidden />
                  </span>
                  <span className="text-white/90"> {row.reasonDetail}.</span>
                </>
              )}
            </p>
          );

          return (
            <li key={row.key} className="overflow-hidden">
              <ActivityStreamRow
                surface={surface}
                profileTo={row.profileTo}
                profileModalState={profileModalState}
                onProfileLinkClick={onProfileLinkClick}
                avatarSrc={row.avatar || ""}
                displayName={row.name}
                xpWeekCrown={(row as { xp_week_crown?: string | null }).xp_week_crown}
                avatarFrame={row.avatarFrame}
                middle={xpMiddle}
                timeLabel={xpRel}
                ctaSlot={
                  isOwnProfileView && surface === "profile" ? (
                    <span className={ctaRailPlaceholderClass} aria-hidden />
                  ) : (
                    <ProfileLink
                      to={row.profileTo}
                      state={profileModalState}
                      className={streamCtaClass}
                      onClick={onProfileLinkClick}
                    >
                      {isOwnProfileView ? "XP" : "PROFILE"}
                    </ProfileLink>
                  )
                }
              />
            </li>
          );
        }

        if (row.kind === "xp_week_podium") {
          const podiumRel = row.awardedAt ? formatTimeAgo(row.awardedAt) : "";
          const podiumMiddle = (
            <p className={streamBodyClass}>
              <span className={streamNameClass}>{isOwnProfileView ? "You" : row.name}</span>
              <span className="text-white/90"> finished </span>
              <span className={streamNameClass}>{row.rankLabel}</span>
              <span className="text-white/90"> on the XP leaderboard for </span>
              <span className={streamNameClass}>{row.periodLabel}</span>
              <span className="text-white/90">.</span>
              {row.xpValue > 0 ? (
                <>
                  <span className="text-white/90"> (</span>
                  <span className="inline-flex items-center gap-0.5 self-center">
                    <span className={streamNameClass}>{formatXp(row.xpValue)}</span>
                    <img src={XP_ICON_SRC} alt="" className="h-3 w-3 shrink-0 object-contain" aria-hidden />
                  </span>
                  <span className="text-white/90">).</span>
                </>
              ) : null}
            </p>
          );

          return (
            <li key={row.key} className="overflow-hidden">
              <ActivityStreamRow
                surface={surface}
                profileTo={row.profileTo}
                profileModalState={profileModalState}
                onProfileLinkClick={onProfileLinkClick}
                avatarSrc={row.avatar || ""}
                displayName={row.name}
                xpWeekCrown={(row as { xp_week_crown?: string | null }).xp_week_crown}
                avatarFrame={row.avatarFrame}
                middle={podiumMiddle}
                timeLabel={podiumRel}
                ctaSlot={
                  <button
                    type="button"
                    className={streamCtaClass}
                    onClick={() => {
                      onProfileLinkClick?.();
                      navigate("/xp-leaderboard");
                    }}
                  >
                    LEADERBOARD
                  </button>
                }
              />
            </li>
          );
        }

        if (row.kind === "digest") {
          const d = row;
          const digestAt = d.digestDisplayAt ?? d.lastPlayedAt;
          const digestRel = digestAt ? formatTimeAgo(digestAt) : "";
          const body =
            d.digestVariant === "streak" ? (
              <p className={streamBodyClass}>
                <span className={streamNameClass}>
                  {isOwnProfileView ? "Your" : d.name}
                </span>
                <span className="text-white/90">
                  {" "}
                  {renderGameToken("TRADER")}
                  {" "}
                  current streak:{" "}
                </span>
                <span className="text-[#d4ff1f]">{d.streakValue ?? 0} wins in a row</span>
                <span className="text-white/90">.</span>
              </p>
            ) : d.digestVariant === "pb" ? (
              <p className={streamBodyClass}>
                <span className={streamNameClass}>
                  {isOwnProfileView ? "Your" : d.name}
                </span>
                <span className="text-white/90">
                  {" "}
                  {renderGameToken("TRADER")}
                  {" "}
                  record:{" "}
                </span>
                <span className="text-[#d4ff1f]">{d.pbValue ?? 0} wins in a row</span>
                <span className="text-white/90">.</span>
              </p>
            ) : d.digestVariant === "bullion_pb" ? (
              <p className={streamBodyClass}>
                <span className={streamNameClass}>
                  {isOwnProfileView ? "Your" : d.name}
                </span>
                <span className="text-white/90"> scored a new personal best on </span>
                {renderGameToken("BULLION")}
                <span className="text-white/90">: </span>
                <span className="text-[#d4ff1f]">{d.bullionCorrectPicks ?? 0} correct picks</span>
                <span className="text-white/90">.</span>
              </p>
            ) : null;

          return (
            <li key={row.key} className="overflow-hidden">
              <ActivityStreamRow
                surface={surface}
                profileTo={d.profileTo}
                profileModalState={profileModalState}
                onProfileLinkClick={onProfileLinkClick}
                avatarSrc={d.avatar || ""}
                displayName={d.name}
                xpWeekCrown={d.xp_week_crown}
                avatarFrame={d.avatarFrame}
                middle={body}
                timeLabel={digestRel}
                ctaSlot={
                  showDigestProfileCta ? (
                    <ProfileLink
                      to={d.profileTo}
                      state={profileModalState}
                      className={streamCtaClass}
                      onClick={onProfileLinkClick}
                    >
                      Profile
                    </ProfileLink>
                  ) : (
                    <span className={ctaRailPlaceholderClass} aria-hidden />
                  )
                }
              />
            </li>
          );
        }

        return null;
      })}
      {!loading &&
      notifications.length === 0 &&
      digests.length === 0 &&
      settledResultRows.length === 0 &&
      xpResultRows.length === 0 &&
      startedPlayingRows.length === 0 ? (
        <li className="px-1 py-4">
          <p className="font-chakra text-[14px] leading-snug text-white/55">{emptyMessage}</p>
        </li>
      ) : null}
      {loadingMore || serverLoadingMore ? (
        <li className="px-1 py-2">
          <p className="font-chakra text-[13px] text-white/60">Loading more…</p>
        </li>
      ) : null}
      {hasMoreRows || hasMoreServerRows ? (
        <li ref={infiniteSentinelRef} className="h-1 w-full" aria-hidden />
      ) : null}
    </ul>
  );
}
